/* eslint-disable no-lonely-if */
import { CopilotActions } from 'actions/actionTypes';

import {
  COPILOT_PROVISIONAL_NEW_CHAT_SESSION_KEY,
  MESSAGE_STATUS_OPTIONS
} from 'constants/copilot';
import {
  mergeCopilotChatDetailMessages,
  normalizeCopilotContextType
} from 'helpers/copilot';

function copilotSessionsDataWithoutProvisionalNewChat(sessionsData) {
  const k = COPILOT_PROVISIONAL_NEW_CHAT_SESSION_KEY;
  if (!sessionsData || !sessionsData[k]) {
    return sessionsData;
  }
  const { [k]: _removed, ...rest } = sessionsData;
  return rest;
}

const initialCopilotState = {
  sessions: {
    data: {}
  },
  /** True after a successful `GET /api/v1/copilot/chats/` (unified list). Cleared on `LOGOUT`. */
  unifiedCopilotChatListLoaded: false,
  /**
   * Per chat: `true` after messages were hydrated (detail GET, merge, or WebSocket), so re-selecting
   * the thread does not call `GET /chats/:uuid` again. Keys removed on `DELETE_COPILOT_CHAT_SUCCESS` / `LOGOUT`.
   */
  unifiedCopilotChatDetailsLoaded: {},
  fetching: false,
  /** `GET /chats/:uuid` in flight (messages hydrate separately from the list). */
  fetchingCopilotChatDetailUuid: null,
  loading: false,
  error: null,
  windowMode: 'embedded',
  /**
   * When true, the next `updateContextFromUrl` tick must not call `emptyCopilotChatContext` for a
   * spurious `hasPathOrMaterialSearchChanged` (e.g. Copilot remount after float → full on meeting).
   * Set by {@link SET_COPILOT_WINDOW_MODE_FROM_SHELL}; cleared after consumption or on empty context.
   */
  urlShellSyncPreserveChat: false,
  chatContext: null,
  /**
   * @-mention omnisearch over WebSocket (`message_type: omnisearch`). `activeRequestId`
   * is the client request id; responses with a different id are dropped as stale.
   * `requestSearchType` mirrors `data.search_type` (Add context tab, or `my_context` for composer
   * @ empty query); `null` for global @-mention omnisearch (non-empty query).
   */
  contextOmnisearch: {
    loading: false,
    activeRequestId: null,
    requestQuery: null,
    requestSearchType: null,
    results: null
  },
  /**
   * Last successful omnisearch `results` by `search_type::query` (see `buildOmnisearchCacheKey` in
   * `actions/copilotOmnisearch.js`). Used to rehydrate without a second WebSocket round-trip when
   * switching Add context tabs or revisiting the same query.
   */
  contextOmnisearchCache: {},
  floatingWindow: {
    position: null,
    size: null
  },
  /**
   * Persisted Add-context / switch-rail mention state for the active chat session (e.g. survives
   * CopilotTextArea remount when toggling embedded ↔ floating). Cleared with chat context / session change.
   */
  composerContextMentionUi: null
};

const copilot = (state = initialCopilotState, action) => {
  switch (action.type) {
    case CopilotActions.SEND_COPILOT_OMNISEARCH: {
      const nextQ = String(action.query ?? '').trim();
      const nextSt = action.searchType ?? null;
      const prevQ = String(
        state.contextOmnisearch?.requestQuery ?? ''
      ).trim();
      const prevSt = state.contextOmnisearch?.requestSearchType ?? null;
      const sameRequestKey = nextQ === prevQ && nextSt === prevSt;
      return {
        ...state,
        contextOmnisearch: {
          ...state.contextOmnisearch,
          loading: true,
          activeRequestId: action.requestId,
          requestQuery: action.query,
          requestSearchType: nextSt,
          // Keep previous hits visible while a refetch of the *same* query+type is in flight (avoids empty flash).
          results: sameRequestKey ? state.contextOmnisearch.results : null
        }
      };
    }
    case CopilotActions.HYDRATE_COPILOT_OMNISEARCH_FROM_CACHE: {
      const nextQ = String(action.requestQuery ?? '').trim();
      const nextSt = action.requestSearchType ?? null;
      return {
        ...state,
        contextOmnisearch: {
          ...state.contextOmnisearch,
          loading: false,
          activeRequestId: null,
          requestQuery: nextQ,
          requestSearchType: nextSt,
          results: action.results
        }
      };
    }
    case CopilotActions.RECEIVE_COPILOT_OMNISEARCH: {
      const responseRequestIdRaw =
        action.data?.requestId ?? action.data?.request_id;
      const responseRequestId =
        responseRequestIdRaw != null ? String(responseRequestIdRaw) : null;
      const primaryId = state.contextOmnisearch?.activeRequestId ?? null;
      if (
        responseRequestId != null &&
        primaryId != null &&
        responseRequestId !== String(primaryId)
      ) {
        return state;
      }
      const {
        requestId: _rid,
        meeting,
        deal,
        user,
        team,
        playlist,
        company,
        contextTypeOrder
      } = action.data || {};
      const resultsPayload = {
        meeting,
        deal,
        user,
        team,
        playlist,
        company,
        contextTypeOrder
      };
      const cacheQ = String(state.contextOmnisearch?.requestQuery ?? '').trim();
      const cacheSt = state.contextOmnisearch?.requestSearchType ?? null;
      const cacheKey = `${cacheSt ?? 'global'}::${cacheQ}`;
      return {
        ...state,
        contextOmnisearch: {
          ...state.contextOmnisearch,
          loading: false,
          results: resultsPayload
        },
        contextOmnisearchCache: {
          ...state.contextOmnisearchCache,
          [cacheKey]: { results: resultsPayload }
        }
      };
    }
    case CopilotActions.CLEAR_COPILOT_OMNISEARCH: {
      if (action.preserveDefaultOmnisearch) {
        return {
          ...state,
          contextOmnisearch: {
            ...state.contextOmnisearch,
            loading: false,
            activeRequestId: null
            // requestQuery + results: keep (cache default empty-query response for the next open)
          }
        };
      }
      return {
        ...state,
        contextOmnisearch: {
          loading: false,
          activeRequestId: null,
          requestQuery: null,
          requestSearchType: null,
          results: null
        },
        contextOmnisearchCache: {}
      };
    }
    case CopilotActions.FETCH_COPILOT_CHAT_REQUEST: {
      return {
        ...state,
        fetching: true,
        loading: true,
        error: null
      };
    }
    case CopilotActions.FETCH_COPILOT_CHAT_SUCCESS: {
      const sessionsFromResponse = {};
      Object.entries(action.response || {}).forEach(([uuid, chat]) => {
        const prev = state.sessions.data[uuid] || {};
        sessionsFromResponse[uuid] = {
          ...prev,
          ...chat,
          contextType: normalizeCopilotContextType(chat.contextType),
          // Unified list often omits messages or returns `[]` per row. Never clobber a hydrated
          // in-memory thread (optimistic user + WebSocket) with an empty list payload.
          messages: (() => {
            if (Array.isArray(chat.messages) && chat.messages.length > 0) {
              return chat.messages;
            }
            if (prev.messages && prev.messages.length > 0) {
              return prev.messages;
            }
            return Array.isArray(chat.messages)
              ? chat.messages
              : (chat.messages ?? prev.messages);
          })()
        };
      });
      const markDetailUuids = action.markDetailLoadedForUuids;
      const nextDetailsLoaded = { ...state.unifiedCopilotChatDetailsLoaded };
      if (Array.isArray(markDetailUuids)) {
        markDetailUuids.forEach(id => {
          if (id) {
            nextDetailsLoaded[id] = true;
          }
        });
      }
      return {
        ...state,
        sessions: {
          ...state.sessions,
          data: {
            ...state.sessions.data,
            ...sessionsFromResponse
          }
        },
        unifiedCopilotChatListLoaded:
          action.unifiedListFetch === true
            ? true
            : state.unifiedCopilotChatListLoaded,
        unifiedCopilotChatDetailsLoaded: nextDetailsLoaded,
        fetching: false,
        loading: false,
        error: null
      };
    }
    case CopilotActions.FETCH_COPILOT_CHAT_DETAIL_REQUEST: {
      return {
        ...state,
        fetchingCopilotChatDetailUuid: action.uuid,
        error: null
      };
    }
    case CopilotActions.FETCH_COPILOT_CHAT_DETAIL_SUCCESS: {
      const { uuid, chat } = action;
      const prev = state.sessions.data[uuid] || {};
      const mergedMessages = mergeCopilotChatDetailMessages(
        prev.messages,
        chat?.messages
      );
      const normalized = {
        ...prev,
        ...chat,
        contextType: normalizeCopilotContextType(chat.contextType),
        messages: mergedMessages
      };
      const clearDetailFetching =
        state.fetchingCopilotChatDetailUuid === uuid;
      return {
        ...state,
        sessions: {
          ...state.sessions,
          data: {
            ...state.sessions.data,
            [uuid]: normalized
          }
        },
        unifiedCopilotChatDetailsLoaded: {
          ...state.unifiedCopilotChatDetailsLoaded,
          [uuid]: true
        },
        fetchingCopilotChatDetailUuid: clearDetailFetching
          ? null
          : state.fetchingCopilotChatDetailUuid,
        error: null
      };
    }
    case CopilotActions.FETCH_COPILOT_CHAT_DETAIL_FAILURE: {
      const clearDetailFetching =
        state.fetchingCopilotChatDetailUuid === action.uuid;
      // Mark detail "settled" so the UI does not spin forever; user can still see empty/error state.
      const failedUuid = action.uuid;
      const nextDetailsLoaded =
        failedUuid != null
          ? {
              ...state.unifiedCopilotChatDetailsLoaded,
              [failedUuid]: true
            }
          : state.unifiedCopilotChatDetailsLoaded;
      return {
        ...state,
        fetchingCopilotChatDetailUuid: clearDetailFetching
          ? null
          : state.fetchingCopilotChatDetailUuid,
        unifiedCopilotChatDetailsLoaded: nextDetailsLoaded,
        error: action.error ?? state.error
      };
    }
    // Do not set `loading` here: `state.copilot.loading` is consumed as `isStreamingMessage` in
    // Copilot and would treat "creating chat" like an in-flight assistant stream (disables chrome,
    // loading affordances) during the POST while the thread already shows an optimistic user row.
    case CopilotActions.CREATE_COPILOT_CHAT_REQUEST: {
      return {
        ...state,
        fetching: false,
        loading: false,
        error: null
      };
    }
    case CopilotActions.UPDATE_COPILOT_CHAT_NAME_REQUEST:
    case CopilotActions.DELETE_COPILOT_CHAT_REQUEST: {
      return {
        ...state,
        fetching: false,
        loading: true,
        error: null
      };
    }
    case CopilotActions.CREATE_COPILOT_CHAT_SUCCESS: {
      const realUuid = action.response.uuid;
      const provKey = COPILOT_PROVISIONAL_NEW_CHAT_SESSION_KEY;
      const provisional = state.sessions.data[provKey];
      const provisionalMessages = Array.isArray(provisional?.messages)
        ? provisional.messages
        : [];
      const mergedMessages = mergeCopilotChatDetailMessages(
        provisionalMessages,
        action.response.messages
      );
      return {
        ...state,
        sessions: {
          ...state.sessions,
          data: {
            ...state.sessions.data,
            [realUuid]: {
              ...action.response,
              messages: mergedMessages,
              contextType: normalizeCopilotContextType(
                action.contextType ?? action.response.contextType
              ),
              contextUuid: action.contextUuid ?? action.response.contextUuid
            }
          }
        },
        fetching: false,
        loading: false,
        error: null
      };
    }
    case CopilotActions.UPDATE_COPILOT_CHAT_NAME_SUCCESS: {
      return {
        ...state,
        sessions: {
          ...state.sessions,
          data: {
            ...state.sessions.data,
            [action.response.uuid]: {
              ...state.sessions.data[action.response.uuid],
              name: action.response.name
            }
          }
        },
        fetching: false,
        loading: false,
        error: null
      };
    }
    case CopilotActions.DELETE_COPILOT_CHAT_SUCCESS: {
      const { [action.uuid]: removedSession, ...remainingSessions } =
        state.sessions.data;
      const { [action.uuid]: _rm, ...remainingDetailsLoaded } =
        state.unifiedCopilotChatDetailsLoaded;

      const shouldClearChatContext =
        state.chatContext != null &&
        state.chatContext.uuid != null &&
        state.chatContext.uuid === action.uuid;

      return {
        ...state,
        sessions: {
          ...state.sessions,
          data: remainingSessions
        },
        unifiedCopilotChatDetailsLoaded: remainingDetailsLoaded,
        fetching: false,
        loading: false,
        error: null,
        ...(shouldClearChatContext
          ? { chatContext: null, composerContextMentionUi: null }
          : {})
      };
    }
    case CopilotActions.CREATE_COPILOT_CHAT_FAILURE: {
      return {
        ...state,
        sessions: {
          ...state.sessions,
          data: copilotSessionsDataWithoutProvisionalNewChat(state.sessions.data)
        },
        fetching: false,
        loading: false,
        error: action.error
      };
    }
    case CopilotActions.FETCH_COPILOT_CHAT_FAILURE:
    case CopilotActions.UPDATE_COPILOT_CHAT_NAME_FAILURE:
    case CopilotActions.DELETE_COPILOT_CHAT_FAILURE: {
      return {
        ...state,
        fetching: false,
        loading: false,
        error: action.error
      };
    }
    case CopilotActions.RECEIVE_COPILOT_MESSAGE: {
      const { chatUuid, uuid } = action.data || {};

      const existingSession = state.sessions.data[chatUuid] || {};
      const messages = existingSession.messages
        ? [...existingSession.messages]
        : [];
      const messageIndex = uuid
        ? messages.findIndex(msg => msg.uuid === uuid)
        : -1;

      const isStreamingMessage =
        action.data.status === MESSAGE_STATUS_OPTIONS.IN_PROGRESS;
      const isThinkingMessage =
        action.data.status === MESSAGE_STATUS_OPTIONS.THINKING;

      // Helper to create a new thinking message
      const createThinkingMessage = msgData => ({
        ...msgData,
        thinkingMessages: [
          {
            message: msgData.message || '',
            timestamp: msgData.created || new Date().toISOString()
          }
        ],
        thinkingStartTime: msgData.created,
        message: ''
      });

      if (uuid) {
        if (messageIndex === -1) {
          // New message — append
          messages.push(
            isThinkingMessage ? createThinkingMessage(action.data) : action.data
          );
        } else {
          // Update existing message
          const existingMessage = messages[messageIndex];

          if (isThinkingMessage) {
            // Append to thinkingMessages array
            const updatedThinkingMessages = existingMessage.thinkingMessages
              ? [...existingMessage.thinkingMessages]
              : [];

            updatedThinkingMessages.push({
              message: action.data.message || '',
              timestamp: action.data.created || new Date().toISOString()
            });

            messages[messageIndex] = {
              ...existingMessage,
              ...action.data,
              thinkingMessages: updatedThinkingMessages,
              thinkingStartTime:
                existingMessage.thinkingStartTime || action.data.created,
              message: existingMessage.message || ''
            };
          } else {
            // Completed message
            const wasThinking =
              existingMessage.status === MESSAGE_STATUS_OPTIONS.THINKING;
            const isNowNotThinking =
              wasThinking &&
              action.data.status !== MESSAGE_STATUS_OPTIONS.THINKING;

            let { thinkingEndTime } = existingMessage;

            if (isNowNotThinking && existingMessage.thinkingMessages?.length) {
              const lastStep =
                existingMessage.thinkingMessages[
                  existingMessage.thinkingMessages.length - 1
                ];
              const completionTime = action.data.created;

              thinkingEndTime =
                new Date(completionTime).getTime() >
                new Date(lastStep.timestamp).getTime()
                  ? completionTime
                  : new Date().toISOString();
            }

            messages[messageIndex] = {
              ...existingMessage,
              ...action.data,
              thinkingMessages: existingMessage.thinkingMessages || [],
              thinkingStartTime: existingMessage.thinkingStartTime,
              thinkingEndTime,
              message: wasThinking
                ? action.data.message || ''
                : (existingMessage.message || '') + (action.data.message || '')
            };
          }
        }
      } else {
        // Optimistic user message or message without UUID
        messages.push(action.data);
      }

      return {
        ...state,
        sessions: {
          ...state.sessions,
          data: {
            ...state.sessions.data,
            [chatUuid]: {
              ...existingSession,
              messages
            }
          }
        },
        unifiedCopilotChatDetailsLoaded: chatUuid
          ? {
              ...state.unifiedCopilotChatDetailsLoaded,
              [chatUuid]: true
            }
          : state.unifiedCopilotChatDetailsLoaded,
        loading: isThinkingMessage || isStreamingMessage
      };
    }
    case CopilotActions.RECEIVE_COPILOT_NAME_UPDATE: {
      return {
        ...state,
        sessions: {
          ...state.sessions,
          data: {
            ...state.sessions.data,
            [action.data.chatUuid]: {
              ...state.sessions.data[action.data.chatUuid],
              name: action.data.name
            }
          }
        }
      };
    }
    case CopilotActions.MESSAGE_TIMEOUT: {
      return {
        ...state,
        fetching: false,
        loading: false
      };
    }
    case CopilotActions.SET_COPILOT_WINDOW_MODE: {
      return {
        ...state,
        windowMode: action.windowMode
      };
    }
    case CopilotActions.SET_COPILOT_WINDOW_MODE_FROM_SHELL: {
      return {
        ...state,
        windowMode: action.windowMode,
        urlShellSyncPreserveChat: true
      };
    }
    case CopilotActions.CONSUME_COPILOT_URL_SHELL_PRESERVE_CHAT_FLAG: {
      return {
        ...state,
        urlShellSyncPreserveChat: false
      };
    }
    case CopilotActions.SET_COPILOT_CHAT_CONTEXT: {
      if (!action.chatContext) {
        return {
          ...state,
          chatContext: null,
          composerContextMentionUi: null,
          sessions: {
            ...state.sessions,
            data: copilotSessionsDataWithoutProvisionalNewChat(state.sessions.data)
          }
        };
      }
      const prevUuid = state.chatContext?.uuid ?? null;
      const nextUuid = action.chatContext.uuid ?? null;
      const sessionChanged = prevUuid !== nextUuid;
      let nextSessionsData = state.sessions.data;
      if (nextUuid && state.sessions.data[COPILOT_PROVISIONAL_NEW_CHAT_SESSION_KEY]) {
        nextSessionsData = copilotSessionsDataWithoutProvisionalNewChat(
          state.sessions.data
        );
      }
      return {
        ...state,
        chatContext: {
          ...action.chatContext,
          contextType: normalizeCopilotContextType(
            action.chatContext.contextType
          )
        },
        sessions: {
          ...state.sessions,
          data: nextSessionsData
        },
        ...(sessionChanged ? { composerContextMentionUi: null } : {})
      };
    }
    case CopilotActions.EMPTY_COPILOT_CHAT_CONTEXT: {
      return {
        ...state,
        chatContext: null,
        composerContextMentionUi: null,
        urlShellSyncPreserveChat: false,
        sessions: {
          ...state.sessions,
          data: copilotSessionsDataWithoutProvisionalNewChat(state.sessions.data)
        }
      };
    }
    case CopilotActions.SET_COPILOT_COMPOSER_CONTEXT_MENTION_UI: {
      return {
        ...state,
        composerContextMentionUi: action.payload
      };
    }
    case CopilotActions.SET_FLOATING_WINDOW_POSITION_AND_SIZE: {
      return {
        ...state,
        floatingWindow: {
          position: action.position ?? state.floatingWindow.position,
          size: action.size ?? state.floatingWindow.size
        }
      };
    }
    default:
      return state;
  }
};

export default copilot;
